/**
 * Deterministic storm timing — the whole synchronisation story for the weather, in one pure function.
 *
 * ZERO WIRE. `stormIntensityAt(epochSeconds)` is a PURE function of a clock every participant already
 * has: no schedule broadcast, no "here comes a storm" packet, no join-in-progress handshake, no state
 * to resync after a reconnect. A client that knows the time knows the weather. The server evaluates it
 * each tick and puts the result in the snapshot header byte that has always been there (it stays the
 * authority for the server-side wind/turn knobs), but not one byte was added for any of this — see the
 * ZERO-WIRE note on STORM in @piratas/protocol.
 *
 * WHY EPOCH AND NOT SIM TIME. Sim time restarts at 0 on every deploy, so a sim-anchored schedule reset
 * the world's weather to calm every time the box was touched, and two processes could disagree. The
 * day/night cycle already learned this lesson (`dayNightPhase`); the storm now shares the anchor.
 *
 * THE SCHEDULE. The epoch is cut into `SLOT_S` slots and each slot k holds EXACTLY ONE storm, whose
 * start offset, span, ramp length and peak fury are drawn from a deterministic integer hash of k. No
 * `Math.random`, no wall-clock read inside, no state: the same k yields the same storm on every device,
 * for ever, in both directions of time. Bounded jitter puts consecutive onsets 45–70 min apart and each
 * storm runs 6–10 min with a 60–90 s ramp at each end.
 *
 * The one-storm-per-slot construction is what keeps evaluation O(1): a storm can never escape its own
 * slot (the jitter and span are bounded well inside `SLOT_S`), so reading the three slots around `t`
 * and taking the strongest is exhaustive — and stays exhaustive if the constants are retuned.
 */
import { AURORA, DAY_NIGHT_CYCLE_S, SEA_STATE, STORM } from '@piratas/protocol';

const smooth01 = (x: number): number => {
  const c = x < 0 ? 0 : x > 1 ? 1 : x;
  return c * c * (3 - 2 * c);
};

/**
 * Deterministic 0..1 draw for slot `k`, stream `salt`. A finalising integer hash (xor-shift + odd
 * multiplies, the splitmix64 finaliser reduced to 32 bits) — not a PRNG, so it needs no state and
 * neighbouring slots decorrelate completely. `Math.imul` keeps it exact 32-bit on every JS engine.
 */
function hash01(k: number, salt: number): number {
  let h = (Math.imul(k | 0, 0x27d4eb2d) ^ Math.imul(salt | 0, 0x165667b1)) >>> 0;
  h ^= h >>> 15; h = Math.imul(h, 0x2545f491) >>> 0;
  h ^= h >>> 13; h = Math.imul(h, 0x3d73373b) >>> 0;
  h ^= h >>> 16;
  return (h >>> 8) / 0x1000000;   // top 24 bits -> [0, 1)
}

/** One scheduled storm, fully determined by its slot index. */
export interface StormWindow {
  /** slot index (the hash key) */
  slot: number;
  /** epoch seconds the ramp-in begins */
  start: number;
  /** total length in seconds, both ramps included */
  span: number;
  /** ramp-in / ramp-out length in seconds */
  ramp: number;
  /** intensity held between the ramps (0..1) */
  peak: number;
}

/**
 * The storm scheduled in slot `k`. Pure, total, and defined for every integer (including negatives) —
 * the schedule extends infinitely in both directions with no origin to drift from.
 */
export function stormWindow(k: number): StormWindow {
  const { SLOT_S, SLOT_JITTER_S, SPAN_MIN_S, SPAN_MAX_S, RAMP_MIN_S, RAMP_MAX_S, PEAK_MIN, MAX_INTENSITY } = STORM;
  const jitter = (hash01(k, 1) * 2 - 1) * SLOT_JITTER_S;
  const span = SPAN_MIN_S + hash01(k, 2) * (SPAN_MAX_S - SPAN_MIN_S);
  // cap the ramp at 40% of the span so the shortest storms still reach their peak instead of
  // ramping straight into ramping back down (which would read as a limp swell that never arrives)
  const ramp = Math.min(RAMP_MIN_S + hash01(k, 3) * (RAMP_MAX_S - RAMP_MIN_S), span * 0.4);
  const peak = PEAK_MIN + hash01(k, 4) * (MAX_INTENSITY - PEAK_MIN);
  return { slot: k, start: k * SLOT_S + SLOT_S * 0.5 + jitter, span, ramp, peak };
}

/** Intensity this particular storm contributes at epoch second `t` (0 outside its window). */
function windowIntensity(w: StormWindow, t: number): number {
  const u = t - w.start;
  if (u <= 0 || u >= w.span) return 0;
  // symmetric ramp-in / ramp-out; both are smoothstep so the derivative is 0 at each end (no kink
  // when the weather starts or stops — the client's EASE_RATE smoothing then has nothing to hide)
  return smooth01(u / w.ramp) * smooth01((w.span - u) / w.ramp) * w.peak;
}

/**
 * Storm intensity (0 calm … STORM.MAX_INTENSITY) at epoch time `t` seconds.
 *
 * Pure: no state, no randomness, no wall-clock read of its own. Same `t` ⇒ same value on every device,
 * which is the entire synchronisation mechanism.
 */
export function stormIntensityAt(t: number): number {
  const k0 = Math.floor(t / STORM.SLOT_S);
  // the neighbours can only matter if the constants are ever retuned past the one-storm-per-slot
  // guarantee; checking them is three hashes and makes the function correct by construction
  let best = 0;
  for (let k = k0 - 1; k <= k0 + 1; k++) {
    const v = windowIntensity(stormWindow(k), t);
    if (v > best) best = v;
  }
  return best;
}

/**
 * Epoch second the next storm begins after `t` (or the current one's start, if a storm is already
 * running). Not used by the sim — this exists so ops/QA can answer "when is the next one?" without
 * scrubbing, and so a test can assert the 45–70 min onset spacing directly.
 */
export function nextStormStart(t: number): number {
  const k0 = Math.floor(t / STORM.SLOT_S);
  for (let k = k0 - 1; k <= k0 + 2; k++) {
    const w = stormWindow(k);
    if (w.start + w.span > t) return w.start;
  }
  return stormWindow(k0 + 2).start;
}

/**
 * AMBIENT SEA STATE — the weather that is there when there is no weather.
 *
 * A storm is a rare event: on the schedule above it runs ~14% of the time, so ~86% of every session is
 * spent on "calm" water. If calm is ONE fixed sea, that is the sea the game actually is, and a player
 * who sails for an hour sees the same water for fifty minutes of it. This is the slow breathing
 * underneath: over tens of minutes the surface drifts between GLASSY (a mirror with a long swell under
 * it) and BREEZY (the same swell, crawling with wind-chop), and it never repeats.
 *
 * WHY IT IS THE CHOP THAT BREATHES, NOT THE SWELL. The long swell is the silhouette the owner signed
 * off on and the thing the ship visibly rides; moving it would change the sea's shape, which is exactly
 * what "enrich, don't replace" forbids. Wind-chop is the part of a real sea that answers the local
 * breeze — it is up within minutes and gone within minutes. So the ambient state rides the chop band's
 * amplitude (and, in the renderer, the cat's-paw contrast and specular glitter that ride on it): the sea
 * keeps its shape and changes its SKIN.
 *
 * NEVER TWICE THE SAME. Three sines whose periods are mutually irrational multiples (they share no
 * common factor, so the sum's true period is astronomical) plus the same integer hash the storm slots
 * use, sampled on a very slow ramp. There is no cycle to learn.
 *
 * Pure function of epoch seconds — same synchronisation story as the storm: every client derives it
 * from a clock it already has, and not one byte crosses the wire.
 */
export function seaStateAt(t: number): number {
  const { PERIOD_A_S, PERIOD_B_S, PERIOD_C_S, DRIFT_S } = SEA_STATE;
  const TAU = Math.PI * 2;
  // three incommensurate swells of "breeziness"…
  let v = 0.5
    + 0.30 * Math.sin(TAU * t / PERIOD_A_S)
    + 0.14 * Math.sin(TAU * t / PERIOD_B_S + 1.7)
    + 0.06 * Math.sin(TAU * t / PERIOD_C_S + 4.1);
  // …plus a very slow hashed walk, so even the sum of sines never quite comes round to itself
  const k = Math.floor(t / DRIFT_S);
  const f = smooth01(t / DRIFT_S - k);
  v += (hash01(k, 91) * (1 - f) + hash01(k + 1, 91) * f - 0.5) * 0.22;
  return v < 0 ? 0 : v > 1 ? 1 : v;
}

/**
 * The chop-band gain to hand the ocean (`setOceanEnv`) at epoch `t`, storm intensity `storm`.
 *
 * Two contributions, deliberately additive rather than multiplied: the ambient breeze sets the baseline,
 * and a storm forces the chop up regardless of what the breeze happened to be doing (a gale does not
 * politely wait for a breezy afternoon). Clamped so the steepness budget the ocean test asserts can
 * never be exceeded by the two stacking.
 */
export function chopGainAt(t: number, storm: number): number {
  const { CHOP_MIN, CHOP_MAX, CHOP_STORM } = SEA_STATE;
  const g = CHOP_MIN + (CHOP_MAX - CHOP_MIN) * seaStateAt(t) + CHOP_STORM * Math.max(0, Math.min(1, storm));
  return g < CHOP_MIN ? CHOP_MIN : g > SEA_STATE.CHOP_CEIL ? SEA_STATE.CHOP_CEIL : g;
}

/* ============================== AURORA BOREALIS — the night's event ==============================
 *
 * Same law as the storm above, one slot up: a PURE function of epoch seconds, zero wire, zero state,
 * survives a deploy, identical on every device that knows what time it is. Read the AURORA block in
 * @piratas/protocol for the design; this file is the arithmetic.
 *
 * THE SLOT IS A NIGHT. A storm can happen at any hour, so its slots are arbitrary. An aurora can only
 * happen at night — and the day/night cycle has already cut the epoch into nights, with midnight at
 * every integer multiple of `DAY_NIGHT_CYCLE_S` (that is exactly what `dayNightPhase` computes). So
 * night `k` is the epoch second `k * DAY_NIGHT_CYCLE_S`, one hashed draw per night decides whether it
 * happens at all, and the display is centred on that night's own midnight. Nearest-integer, not floor:
 * a night straddles the cycle boundary, so `Math.round` is what puts `t` in the night it is actually in.
 *
 * The salts below (11…17) are disjoint from the storm's (1…4) and the sea state's (91) so the two
 * schedules cannot correlate — an aurora must not prefer the nights a storm was already going to skip.
 */
/** One scheduled display, fully determined by its night index. */
export interface AuroraWindow {
  /** night index (the hash key): the display is centred on epoch second `night * DAY_NIGHT_CYCLE_S` */
  night: number;
  /** false ⇒ this night simply has no aurora (the common case — see AURORA.NIGHT_CHANCE) */
  active: boolean;
  /** epoch seconds the ramp-in begins */
  start: number;
  /** total length in seconds, both ramps included */
  span: number;
  /** ramp-in / ramp-out length in seconds */
  ramp: number;
  /** intensity held between the ramps (0..1) */
  peak: number;
  /** epoch seconds the substorm breakup is centred on */
  breakup: number;
  /** how long that breakup lasts (s) */
  breakupSpan: number;
}

/**
 * The aurora scheduled for night `k`. Pure, total, defined for every integer including negatives — the
 * schedule extends infinitely in both directions with no origin to drift from.
 */
export function auroraWindow(k: number): AuroraWindow {
  const {
    NIGHT_CHANCE, CENTER_JITTER_S, SPAN_MIN_S, SPAN_MAX_S, RAMP_MIN_S, RAMP_MAX_S,
    PEAK_MIN, MAX_INTENSITY, BREAKUP_MIN_S, BREAKUP_MAX_S, BREAKUP_AT_MIN, BREAKUP_AT_MAX,
  } = AURORA;
  const active = hash01(k, 11) < NIGHT_CHANCE;
  const centre = k * DAY_NIGHT_CYCLE_S + (hash01(k, 12) * 2 - 1) * CENTER_JITTER_S;
  const span = SPAN_MIN_S + hash01(k, 13) * (SPAN_MAX_S - SPAN_MIN_S);
  // the ramp is capped at 40% of the span for the same reason the storm's is: a display that ramps
  // straight into ramping back down never reaches its own peak and reads as a glow that failed
  const ramp = Math.min(RAMP_MIN_S + hash01(k, 14) * (RAMP_MAX_S - RAMP_MIN_S), span * 0.4);
  const peak = PEAK_MIN + hash01(k, 15) * (MAX_INTENSITY - PEAK_MIN);
  const start = centre - span * 0.5;
  const breakupSpan = BREAKUP_MIN_S + hash01(k, 16) * (BREAKUP_MAX_S - BREAKUP_MIN_S);
  const breakup = start + span * (BREAKUP_AT_MIN + hash01(k, 17) * (BREAKUP_AT_MAX - BREAKUP_AT_MIN));
  return { night: k, active, start, span, ramp, peak, breakup, breakupSpan };
}

/** Envelope this particular display contributes at epoch second `t` (0 outside its window). */
function auroraEnvelope(w: AuroraWindow, t: number): number {
  if (!w.active) return 0;
  const u = t - w.start;
  if (u <= 0 || u >= w.span) return 0;
  return smooth01(u / w.ramp) * smooth01((w.span - u) / w.ramp) * w.peak;
}

/** The breakup pulse (0..1) this display contributes at `t` — a smooth bump, zero outside its beat. */
function auroraBreakupPulse(w: AuroraWindow, t: number): number {
  if (!w.active) return 0;
  const half = w.breakupSpan * 0.5;
  const x = Math.abs(t - w.breakup) / half;
  return x >= 1 ? 0 : smooth01(1 - x);
}

/** How much a storm at intensity `s` has left of the aurora: 1 clear … 0 at AURORA.STORM_KILL. */
function stormMask(s: number): number {
  const m = 1 - s / AURORA.STORM_KILL;
  return m < 0 ? 0 : m > 1 ? 1 : m;
}

/**
 * Aurora intensity (0 none … AURORA.MAX_INTENSITY) at epoch time `t` seconds — the whole schedule in
 * one pure call. Same `t` ⇒ same value on every device, for ever, which IS the synchronisation.
 *
 * The storm is folded in here rather than left to each consumer: a cloud deck and an aurora on screen
 * together is a light leak, and a rule that lives in one place cannot be forgotten in the other.
 * Reading three nights is exhaustive by construction (a display cannot escape its own night: the
 * jitter plus half a span is far inside DAY_NIGHT_CYCLE_S) and stays exhaustive if the constants move.
 */
export function auroraIntensityAt(t: number): number {
  const k0 = Math.round(t / DAY_NIGHT_CYCLE_S);
  let best = 0;
  for (let k = k0 - 1; k <= k0 + 1; k++) {
    const v = auroraEnvelope(auroraWindow(k), t);
    if (v > best) best = v;
  }
  return best === 0 ? 0 : best * stormMask(stormIntensityAt(t));
}

/**
 * The substorm BREAKUP beat (0 quiet … 1 full breakup) at epoch time `t`. Spent on MOTION — ray speed,
 * fold rate, how nervous the curtain is — never on brightness: a brightness spike is a bloom accident,
 * whereas "the whole sky suddenly moves" is what people actually remember about seeing one.
 */
export function auroraBreakupAt(t: number): number {
  const k0 = Math.round(t / DAY_NIGHT_CYCLE_S);
  let best = 0;
  for (let k = k0 - 1; k <= k0 + 1; k++) {
    const w = auroraWindow(k);
    // gated on the envelope so a breakup can never survive the display it belongs to
    const v = auroraBreakupPulse(w, t) * (auroraEnvelope(w, t) > 0.02 ? 1 : 0);
    if (v > best) best = v;
  }
  return best === 0 ? 0 : best * stormMask(stormIntensityAt(t));
}

/**
 * Epoch second the next display begins after `t` (or the current one's start, if one is already
 * running). Not used by the sim — this exists so ops/QA can answer "when is the next one?" without
 * scrubbing, and so a test can assert the rarity directly.
 */
export function nextAuroraStart(t: number): number {
  const k0 = Math.round(t / DAY_NIGHT_CYCLE_S);
  for (let k = k0 - 1; k < k0 + 400; k++) {
    const w = auroraWindow(k);
    if (w.active && w.start + w.span > t) return w.start;
  }
  return Number.POSITIVE_INFINITY;
}
